import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { ProfileData } from "@/hooks/useProfileSettings";
import { useTranslation } from "react-i18next";
import { currencyMap, COUNTRIES } from "@/constants/currencies";

interface PersonalInfoFormProps {
  profile: ProfileData | null;
  onFieldChange: (field: string, value: string) => void;
}

const PersonalInfoForm = ({ profile, onFieldChange }: PersonalInfoFormProps) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(profile?.first_name || "");
  const [lastName, setLastName] = useState(profile?.last_name || "");
  const [country, setCountry] = useState(profile?.country || "Kenya");
  const [currency, setCurrency] = useState(profile?.currency || "KES");
  const [phone, setPhone] = useState(profile?.whatsapp_number || "");
  const [countrySearchQuery, setCountrySearchQuery] = useState("");

  // Function to get appropriate currency for a country
  const getCountryCurrency = (country: string): string => {
    return currencyMap[country] || "USD"; // Default to USD if country not found
  };

  // Update currency when country changes
  useEffect(() => {
    if (country) {
      const countryCurrency = getCountryCurrency(country);
      setCurrency(countryCurrency);
      onFieldChange("currency", countryCurrency);
      console.log(`Country changed to ${country}, setting currency to: ${countryCurrency}`);
    }
  }, [country]);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    onFieldChange("firstName", e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
    onFieldChange("lastName", e.target.value);
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    onFieldChange("country", value);
  };

  const handleCurrencyChange = (value: string) => {
    setCurrency(value);
    onFieldChange("currency", value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
    onFieldChange("phone", e.target.value);
  };

  // Filter countries based on search query
  const filteredCountries = COUNTRIES.filter(c => 
    c.toLowerCase().includes(countrySearchQuery.toLowerCase())
  );

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">{t('settings.firstName')}</Label>
          <Input 
            id="firstName" 
            value={firstName} 
            onChange={handleFirstNameChange}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">{t('settings.lastName')}</Label>
          <Input 
            id="lastName" 
            value={lastName} 
            onChange={handleLastNameChange}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label htmlFor="country">{t('settings.country')}</Label>
          <Select 
            value={country} 
            onValueChange={handleCountryChange}
          >
            <SelectTrigger id="country">
              <SelectValue placeholder={t('settings.selectCountry')} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              <div className="px-2 py-2">
                <Input
                  placeholder={t('settings.searchCountries')}
                  className="mb-2"
                  value={countrySearchQuery}
                  onChange={(e) => setCountrySearchQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {filteredCountries.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-gray-500">
                  {t('settings.noCountriesFound')}
                </div>
              ) : (
                <SelectGroup>
                  <SelectLabel>{t('settings.countries')}</SelectLabel>
                  {filteredCountries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">{t('settings.phoneNumber')}</Label>
          <Input 
            id="phone" 
            value={phone} 
            onChange={handlePhoneChange}
            type="tel"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="currency">{t('settings.currency')}</Label>
          <Select 
            value={currency} 
            onValueChange={handleCurrencyChange}
          >
            <SelectTrigger id="currency">
              <SelectValue placeholder={t('settings.selectCurrency')} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              <SelectGroup>
                <SelectLabel>{t('settings.africanCurrencies')}</SelectLabel>
                <SelectItem value="KES">KES (Kenyan Shilling)</SelectItem>
                <SelectItem value="ETB">ETB (Ethiopian Birr)</SelectItem>
                <SelectItem value="NGN">NGN (Nigerian Naira)</SelectItem>
                <SelectItem value="ZAR">ZAR (South African Rand)</SelectItem>
                <SelectItem value="GHS">GHS (Ghanaian Cedi)</SelectItem>
                <SelectItem value="TZS">TZS (Tanzanian Shilling)</SelectItem>
                <SelectItem value="RWF">RWF (Rwandan Franc)</SelectItem>
                <SelectItem value="UGX">UGX (Ugandan Shilling)</SelectItem>
                <SelectItem value="XOF">XOF (West African CFA Franc)</SelectItem>
                <SelectItem value="DZD">DZD (Algerian Dinar)</SelectItem>
                <SelectItem value="AOA">AOA (Angolan Kwanza)</SelectItem>
                <SelectItem value="BWP">BWP (Botswanan Pula)</SelectItem>
                <SelectItem value="BIF">BIF (Burundian Franc)</SelectItem>
                <SelectItem value="CVE">CVE (Cape Verdean Escudo)</SelectItem>
                <SelectItem value="XAF">XAF (Central African CFA Franc)</SelectItem>
                <SelectItem value="KMF">KMF (Comorian Franc)</SelectItem>
                <SelectItem value="CDF">CDF (Congolese Franc)</SelectItem>
                <SelectItem value="DJF">DJF (Djiboutian Franc)</SelectItem>
                <SelectItem value="EGP">EGP (Egyptian Pound)</SelectItem>
                <SelectItem value="ERN">ERN (Eritrean Nakfa)</SelectItem>
                <SelectItem value="SZL">SZL (Swazi Lilangeni)</SelectItem>
                <SelectItem value="GMD">GMD (Gambian Dalasi)</SelectItem>
                <SelectItem value="GNF">GNF (Guinean Franc)</SelectItem>
                <SelectItem value="LSL">LSL (Lesotho Loti)</SelectItem>
                <SelectItem value="LRD">LRD (Liberian Dollar)</SelectItem>
                <SelectItem value="LYD">LYD (Libyan Dinar)</SelectItem>
                <SelectItem value="MGA">MGA (Malagasy Ariary)</SelectItem>
                <SelectItem value="MWK">MWK (Malawian Kwacha)</SelectItem>
                <SelectItem value="MRU">MRU (Mauritanian Ouguiya)</SelectItem>
                <SelectItem value="MUR">MUR (Mauritian Rupee)</SelectItem>
                <SelectItem value="MAD">MAD (Moroccan Dirham)</SelectItem>
                <SelectItem value="MZN">MZN (Mozambican Metical)</SelectItem>
                <SelectItem value="NAD">NAD (Namibian Dollar)</SelectItem>
                <SelectItem value="STN">STN (São Tomé and Príncipe Dobra)</SelectItem>
                <SelectItem value="SCR">SCR (Seychellois Rupee)</SelectItem>
                <SelectItem value="SLL">SLL (Sierra Leonean Leone)</SelectItem>
                <SelectItem value="SOS">SOS (Somali Shilling)</SelectItem>
                <SelectItem value="SSP">SSP (South Sudanese Pound)</SelectItem>
                <SelectItem value="SDG">SDG (Sudanese Pound)</SelectItem>
                <SelectItem value="TND">TND (Tunisian Dinar)</SelectItem>
                <SelectItem value="ZMW">ZMW (Zambian Kwacha)</SelectItem>
                <SelectItem value="ZWL">ZWL (Zimbabwean Dollar)</SelectItem>
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>{t('settings.globalCurrencies')}</SelectLabel>
                <SelectItem value="USD">USD (US Dollar)</SelectItem>
                <SelectItem value="EUR">EUR (Euro)</SelectItem>
                <SelectItem value="GBP">GBP (British Pound)</SelectItem>
                <SelectItem value="CAD">CAD (Canadian Dollar)</SelectItem>
                <SelectItem value="AUD">AUD (Australian Dollar)</SelectItem>
                <SelectItem value="JPY">JPY (Japanese Yen)</SelectItem>
                <SelectItem value="CHF">CHF (Swiss Franc)</SelectItem>
                <SelectItem value="CNY">CNY (Chinese Yuan)</SelectItem>
                <SelectItem value="INR">INR (Indian Rupee)</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            {country ? t('settings.currencyAutoSet', { country }) : t('settings.currencyDescription')}
          </p>
        </div>
      </div>
    </>
  );
};

export default PersonalInfoForm;
